// 浏览器指纹识别工具
export interface BrowserFingerprint {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  timezone: string;
  cookieEnabled: boolean;
  doNotTrack: string | null;
  canvasFingerprint: string;
  webglFingerprint: string;
  hash: string;
}

class BrowserFingerprintService {
  private static instance: BrowserFingerprintService;
  private fingerprint: BrowserFingerprint | null = null;

  private constructor() {}

  public static getInstance(): BrowserFingerprintService {
    if (!BrowserFingerprintService.instance) {
      BrowserFingerprintService.instance = new BrowserFingerprintService();
    }
    return BrowserFingerprintService.instance;
  }

  // 生成浏览器指纹
  public generateFingerprint(): BrowserFingerprint {
    if (this.fingerprint) {
      return this.fingerprint;
    }

    const fingerprint: BrowserFingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      canvasFingerprint: this.generateCanvasFingerprint(),
      webglFingerprint: this.generateWebGLFingerprint(),
      hash: '',
    };

    // 生成哈希值
    fingerprint.hash = this.generateHash(fingerprint);
    this.fingerprint = fingerprint;

    return fingerprint;
  }

  // 生成Canvas指纹
  private generateCanvasFingerprint(): string {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';

      canvas.width = 200;
      canvas.height = 50;

      // 绘制一些文本和图形
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Browser Fingerprint', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Browser Fingerprint', 4, 17);

      return canvas.toDataURL();
    } catch (error) {
      console.error('Error generating canvas fingerprint:', error);
      return '';
    }
  }

  // 生成WebGL指纹
  private generateWebGLFingerprint(): string {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return '';

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) return '';

      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

      return `${vendor}~${renderer}`;
    } catch (error) {
      console.error('Error generating WebGL fingerprint:', error);
      return '';
    }
  }

  // 生成简单的哈希值
  private generateHash(fingerprint: Omit<BrowserFingerprint, 'hash'>): string {
    const str = JSON.stringify(fingerprint);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 转换为32位整数
    }
    return Math.abs(hash).toString(36);
  }

  // 获取当前指纹
  public getFingerprint(): BrowserFingerprint {
    return this.generateFingerprint();
  }

  // 检查是否为已知浏览器
  public isKnownBrowser(): boolean {
    const fingerprint = this.getFingerprint();
    const storedFingerprints = this.getStoredFingerprints();
    return storedFingerprints.includes(fingerprint.hash);
  }

  // 存储当前指纹
  public storeFingerprint(): void {
    const fingerprint = this.getFingerprint();
    const storedFingerprints = this.getStoredFingerprints();
    
    if (!storedFingerprints.includes(fingerprint.hash)) {
      storedFingerprints.push(fingerprint.hash);
      localStorage.setItem('browser-fingerprints', JSON.stringify(storedFingerprints));
    }
  }

  // 获取存储的指纹列表
  private getStoredFingerprints(): string[] {
    try {
      const stored = localStorage.getItem('browser-fingerprints');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading stored fingerprints:', error);
      return [];
    }
  }

  // 清除指纹记录
  public clearFingerprints(): void {
    localStorage.removeItem('browser-fingerprints');
  }

  // 获取指纹信息（用于调试）
  public getFingerprintInfo(): {
    current: BrowserFingerprint;
    isKnown: boolean;
    storedCount: number;
  } {
    const current = this.getFingerprint();
    const isKnown = this.isKnownBrowser();
    const storedCount = this.getStoredFingerprints().length;

    return {
      current,
      isKnown,
      storedCount,
    };
  }
}

export const browserFingerprintService = BrowserFingerprintService.getInstance();
